import { useEffect, useRef } from 'react';
import { useReduxState } from 'hooks';
import { shuffleArray } from 'shared-helpers/common';
import { Button, SortableList } from 'components/common';
import { Form, FormGroup, RadioGroup } from 'components/common/form';
import { ReaderBlockSequencingOption } from 'components/reader/blocks/sequencing';

import type { FormApi } from 'informed';
import type { Block, BlockSequencing, Course } from 'shared-types';

interface Props {
  blockId: Block['id'];
  data: BlockSequencing;
  isPreview: boolean;
  languageVersion: Course['language'];
  onUseRandomOrderChange: (value: boolean) => void;
  updateData: (data: BlockSequencing) => void;
}

const EditorBlockSequencingOrder = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  onUseRandomOrderChange,
  updateData
}: Props) => {
  const { messages } = useReduxState();

  const formApiRef = useRef<FormApi>(null);

  useEffect(() => {
    if (formApiRef.current) {
      const { useRandomOrder } = formApiRef.current.getState().values;
      if (data.useRandomOrder !== useRandomOrder) {
        formApiRef.current.setValue('useRandomOrder', data.useRandomOrder);
      }
    }
  }, [data.useRandomOrder]);

  const randomize = () => {
    data.optionIds = shuffleArray(data.optionIds);
    updateData(data);
  };

  const sort = (ids: string[]) => updateData({ ...data, optionIds: ids });

  const setFormApi = (api: FormApi) => {
    formApiRef.current = api;
  };

  return (
    <div className="order">
      <Form getApi={setFormApi}>
        <FormGroup label={messages.editorBlockSequencingUserOrder}>
          <RadioGroup
            field="useRandomOrder"
            initialValue={data.useRandomOrder || false}
            onChange={onUseRandomOrderChange}
            options={[
              {
                disabled: isPreview,
                id: `editor-block-sequencing-use-random-order-yes-${blockId}`,
                label: messages.editorBlockSequencingUseRandomOrder,
                value: true
              },
              {
                disabled: isPreview,
                id: `editor-block-sequencing-use-random-order-no-${blockId}`,
                label: messages.editorBlockSequencingSetOrderManually,
                value: false
              }
            ]}
          />
        </FormGroup>
      </Form>

      <div
        className={`preview ${data?.useRandomOrder ? '' : 'preview-visible'}`}
      >
        <SortableList
          items={data.optionIds}
          itemStyles={{ backgroundColor: 'transparent', padding: 0 }}
          listId={`sequencing-options-order-${blockId}`}
          onChange={sort}
          renderItem={(id) => {
            const localizedText =
              data.locales[languageVersion].translatable[`option_${id}_text`] ||
              '';

            return (
              <ReaderBlockSequencingOption key={id} text={localizedText} />
            );
          }}
          syncWithParent={true}
        />

        {!isPreview && (
          <div className="randomize-button">
            <Button
              category="light"
              icon="sync-alt"
              onClick={randomize}
              size="medium"
              text={messages.editorBlockSequencingReorder}
            />
          </div>
        )}
      </div>

      <style jsx>{`
        .order {
          margin-top: 20px;
        }
        .preview {
          display: none;
          margin-top: 10px;
        }
        .preview-visible {
          display: block;
        }
        .randomize-button {
          margin-top: 5px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockSequencingOrder;
